/**
 * SelectedSpaceBar
 *
 * Thin wrapper around `<SpaceBar>` that:
 *   - reads `selectedSpace` from the conversation-assistant ui-store (set when user clicks a space
 *     in the side-nav to start a new scoped chat)
 *   - falls back to looking up the space by `rovoSpaceId` on the current conversation so the bar
 *     remains visible when navigating into an existing space-scoped conversation
 *   - is gated behind the Rovo Spaces experiment
 *   - returns `null` when no active space can be resolved or the experiment is off
 *
 * Why this exists:
 *   Both the main-view branch and the mini-modal branch of `ConversationAssistantRender`
 *   need to show the same Rovo Space bar. Inlining the same JSX in two places caused this
 *   to be missing in mini-modal mode (the original wiring in PR #350573 only landed in
 *   `renderMainView()`). Extracting the gate + store read into a single component keeps the
 *   two render paths in sync and prevents future drift.
 */
import React from 'react';

import { di } from 'react-magnetic-di';
import { shallowEqualObjects } from 'shallow-equal';

import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import {
	useSelectedSpaceSelector,
	useUIStore,
	useUIStoreActions,
} from '@atlassian/conversation-assistant-store/controllers/ui-store';
import { useSpaces } from '@atlassian/rovo-spaces/controllers/space-store';
import { SpaceBar } from '@atlassian/rovo-spaces/ui/SpaceBar';

import { useCurrentConversation } from '../../controllers/chat-store/useCurrentConversation';

export type SelectedSpaceBarProps = {
	/**
	 * Cloud / site id, forwarded to `SpaceBar` for emoji + manage-modal context.
	 * Required because `SpaceBar` itself requires a `cloudId: string` to resolve
	 * site-specific emojis via the emoji provider.
	 */
	cloudId: string;
	/** Emoji provider used by `SpaceBar` to resolve the space emoji. */
	emojiProvider?: React.ComponentProps<typeof SpaceBar>['emojiProvider'];
};

type SelectedSpaceBarInnerProps = SelectedSpaceBarProps & {
	/** `selectedSpace` read from the ui-store, threaded in by the gate wrappers below. */
	selectedSpace: ReturnType<typeof useSelectedSpaceSelector>[0]['selectedSpace'];
};

const SelectedSpaceBarComponent = ({
	cloudId,
	emojiProvider,
	selectedSpace,
}: SelectedSpaceBarInnerProps): JSX.Element | null => {
	const { setActiveMenu, setSelectedSpace } = useUIStoreActions();
	const currentConversation = useCurrentConversation();
	const spaces = useSpaces();

	// Prefer the explicitly-selected space from the ui-store (new chat flow).
	// Fall back to looking up the space by rovoSpaceId on the current conversation
	// so the bar remains visible when navigating into an existing space-scoped conversation.
	const activeSpace =
		selectedSpace ??
		(currentConversation?.rovoSpaceId
			? spaces.find((s) => s.id === currentConversation.rovoSpaceId)
			: undefined);

	if (
		!activeSpace ||
		!FeatureGates.getExperimentValue('rovo_chat_spaces_experiment', 'isEnabled', false)
	) {
		return null;
	}

	const handleOpenSpace = () => {
		setSelectedSpace({
			id: activeSpace.id,
			name: activeSpace.name,
			emoji: activeSpace.emoji,
			entryPoint: selectedSpace?.entryPoint,
		});
		setActiveMenu('space-landing');
	};

	return (
		<SpaceBar
			emoji={activeSpace.emoji}
			emojiProvider={emojiProvider}
			name={activeSpace.name}
			cloudId={cloudId}
			onClick={handleOpenSpace}
		/>
	);
};

// rovo_ttai_segment_render_stabilization: absorb parent render waves when props are stable.
const MemoizedSelectedSpaceBar = React.memo(SelectedSpaceBarComponent, shallowEqualObjects);

// rovo_ttai_segment_render_stabilization ON: subscribe to `selectedSpace` via the
// narrow selector so the component only re-renders when `selectedSpace` changes,
// rather than on every UI-store state change.
const SelectedSpaceBarWithSelector = (props: SelectedSpaceBarProps): JSX.Element | null => {
	di(useSelectedSpaceSelector);
	const [{ selectedSpace }] = useSelectedSpaceSelector();
	return <MemoizedSelectedSpaceBar {...props} selectedSpace={selectedSpace} />;
};

// rovo_ttai_segment_render_stabilization OFF/control: subscribe to the broad UI
// store, matching the prior behaviour where the component re-rendered on any
// UI-store state change.
const SelectedSpaceBarWithBroadStore = (props: SelectedSpaceBarProps): JSX.Element | null => {
	di(useUIStore);
	const { selectedSpace } = useUIStore();
	return <SelectedSpaceBarComponent {...props} selectedSpace={selectedSpace} />;
};

export const SelectedSpaceBar = (props: SelectedSpaceBarProps): JSX.Element | null => {
	return FeatureGates.getExperimentValue(
		'rovo_ttai_segment_render_stabilization',
		'isEnabled',
		false,
	) ? (
		<SelectedSpaceBarWithSelector {...props} />
	) : (
		<SelectedSpaceBarWithBroadStore {...props} />
	);
};
